<template>
    <div>

        <!-- Breadcrumb navigace (zobrazí se jen uvnitř složky) -->
        <div v-if="parent && parent.name" class="files-breadcrumb mb-4">
            <button class="btn cert-list__view-btn btn-sm" @click="getFoldersList()">
                <i class="bi bi-house-door me-1"></i>{{ $t('file.myFiles') }}
            </button>
            <span class="text-muted mx-2">/</span>
            <template v-if="parent.folders_path">
                <span v-for="ancestor in [...parent.folders_path].reverse()" :key="ancestor.uuid">
                    <button class="btn cert-list__view-btn btn-sm" @click="getFoldersList(ancestor.uuid)">
                        {{ ancestor.name }}
                    </button>
                    <span class="text-muted mx-2">/</span>
                </span>
            </template>
            <span class="fw-semibold" style="font-size:14px;">{{ parent.name }}</span>
        </div>

        <!-- Složky jako karty -->
        <div v-if="folders.length" class="folder-cards-row mb-4">
            <div
                v-for="folder in folders"
                :key="folder.uuid"
                class="folder-card dashboard-action-box--clickable"
                @click="getFoldersList(folder.uuid)"
            >
                <div class="folder-card__icon">
                    <img :src="iconFolderBlue" alt="">
                </div>
                <p class="folder-card__name">{{ folder.name }}</p>
                <p class="folder-card__count">&nbsp;</p>
            </div>
        </div>

        <Skeletor v-if="loading" />

        <!-- Prázdný stav -->
        <div v-if="!loading && !folders.length && !regularFiles.length" class="text-muted" style="font-size:14px;">
            {{ $t('general.noRecord') }}
        </div>

        <!-- Seznam souborů -->
        <div v-if="!loading && regularFiles.length" class="cert-list">
            <div
                class="cert-list__row"
                v-for="file in regularFiles"
                :key="file.uuid"
            >
                <div class="cert-list__icon-wrap">
                    <img :src="iconFile" alt="" class="cert-list__file-icon">
                </div>

                <div class="cert-list__col">
                    <span class="cert-list__label">{{ $t('file.fileName') }}</span>
                    <span class="cert-list__value">{{ file.name }}</span>
                </div>

                <div class="cert-list__col cert-list__col--date">
                    <span class="cert-list__label">{{ $t('file.fileCreatedDate') }}</span>
                    <span class="cert-list__value">{{ formatDateShort(file.created_at) }}, {{ formatTime(file.created_at) }}</span>
                </div>

                <div class="cert-list__action">
                    <router-link
                        class="btn cert-list__view-btn"
                        :to="{ name: 'FileDetail', params: { uuid: file.uuid } }"
                    >
                        {{ $t('file.viewFile') }}
                    </router-link>
                </div>
            </div>
        </div>

        <!-- Načíst více -->
        <div v-if="!loading && loadMoreAvailable" class="d-flex justify-content-center mt-4">
            <button class="btn cert-list__view-btn" @click="loadMore(parent?.id)">
                {{ $t('file.loadMore') }}
            </button>
        </div>

        <!-- Modal Vytvořit složku (nový design) -->
        <div v-if="showCreateFolderModal" class="create-cert-overlay" @click.self="showCreateFolderModal = false">
            <div class="create-cert-modal" style="max-width:400px;">
                <div class="create-cert-modal__header">
                    <span class="create-cert-modal__title">{{ $t('file.newFolder') }}</span>
                    <button class="create-cert-modal__close" @click="showCreateFolderModal = false">×</button>
                </div>
                <div class="create-cert-modal__body">
                    <label class="create-cert-label mb-1">{{ $t('file.name') }}</label>
                    <input
                        type="text"
                        class="form-control mb-4"
                        v-model="newFolderName"
                        :placeholder="$t('file.newFolder')"
                        @keyup.enter="createFolder"
                    >
                    <div class="d-flex justify-content-end">
                        <button class="btn btn-primary" @click="createFolder" :disabled="!newFolderName">
                            {{ $t('file.create') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import http from '@/http';
import iconFile from '/public/img/new-design/icons/file.svg';
import iconFolderBlue from '/public/img/new-design/icons/folder-fill-blue.svg';

export default {
    props: {
        selectedFolderUuid: {
            type: String,
            default: null,
        },
    },

    mounted() {
        this.getFoldersList();
        this.emitter.on('fileUploaded', (raw) => {
            const file = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (file && file.uuid) {
                this.getFileDetail(file.uuid);
            }
        });
        this.emitter.on('certAdded', (newCert) => {
            if (newCert?.file?.uuid) {
                this.getFileDetail(newCert.file.uuid);
            }
        });
    },

    data() {
        return {
            files: [],
            parent: null,
            currentUuid: null,
            newFolderName: '',
            showCreateFolderModal: false,
            loading: true,
            page: 1,
            perPage: 20,
            loadMoreAvailable: false,
            iconFile,
            iconFolderBlue,
        };
    },

    computed: {
        folders() {
            return this.files.filter(f => f.type === 'FOLDER');
        },

        regularFiles() {
            return this.files.filter(f => f.type !== 'FOLDER');
        },
    },

    methods: {
        getFoldersList(uuid, append = false) {
            const path = uuid ? `/files/folders/${uuid}` : '/files/folders';
            const params = { perPage: this.perPage, page: this.page };

            if (!append) {
                this.loading = true;
                this.page = 1;
            }

            http.get(path, { params }).then((response) => {
                this.parent = response.data.folder ?? null;
                this.currentUuid = uuid ?? null;

                const items = response.data.files || [];

                if (append) {
                    this.files = this.files.concat(items);
                } else {
                    this.files = items;
                }

                this.loadMoreAvailable = items.length === this.perPage;
                this.loading = false;
            });
        },

        loadMore(parentId) {
            this.page++;
            this.getFoldersList(parentId ?? this.currentUuid, true);
        },

        createFolder() {
            if (!this.newFolderName) return;

            const body = {
                name: this.newFolderName,
                uuid: this.currentUuid ?? null,
            };

            http.post('/files/folders/create', body).then((response) => {
                if (response.data.file) {
                    this.files = [response.data.file, ...this.files].sort((a, b) => {
                        const ta = a.type ?? '';
                        const tb = b.type ?? '';
                        const cmp = tb.localeCompare(ta);
                        return cmp !== 0 ? cmp : (a.name ?? '').localeCompare(b.name ?? '');
                    });
                }
                this.showCreateFolderModal = false;
                this.newFolderName = '';
            });
        },

        getFileDetail(uuid) {
            http.get(`/files/${uuid}/detail`).then((response) => {
                if (response.data.file) {
                    this.files.push(response.data.file);
                }
            });
        },

        openCreateFolderModal() {
            this.showCreateFolderModal = true;
        },

        formatDateShort(dateStr) {
            const d = new Date(dateStr);
            const days = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
            return `${days[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
        },

        formatTime(dateStr) {
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
    },
};
</script>

<style scoped>
.files-breadcrumb {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    padding: 10px 0;
    font-size: 14px;
}
</style>